<script setup>
import { computed, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { useVoiceCallStatus } from 'dashboard/composables/useVoiceCallStatus';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { isAInboxViewRoute } from 'dashboard/helper/routeHelpers';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import Avatar from 'next/avatar/Avatar.vue';
import VipBadge from 'dashboard/components-next/VipBadge.vue';
import MessagePreview from './MessagePreview.vue';
import InboxName from '../InboxName.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import PriorityMark from './PriorityMark.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';

const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  issueId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  conversationType: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
  isCollapsed: { type: Boolean, default: false },
  isQaMonitor: { type: Boolean, default: false },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
]);

const router = useRouter();
const route = useRoute();
const store = useStore();

const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({
  x: null,
  y: null,
});

const currentChat = useMapGetter('getSelectedChat');
const inboxesList = useMapGetter('inboxes/getInboxes');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');

const chatMetadata = computed(() => props.chat.meta || {});

const assignee = computed(() => chatMetadata.value.assignee || {});

const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() => {
  return senderId.value
    ? store.getters['contacts/getContact'](senderId.value)
    : {};
});

const isActiveChat = computed(() => {
  return currentChat.value.id === props.chat.id;
});

const hasUnread = computed(() => (props.chat.unread_count || 0) > 0);

const isInboxNameVisible = computed(() => !activeInbox.value);

const lastMessageInChat = computed(() => getLastMessage(props.chat));

const callStatus = computed(
  () => props.chat.additional_attributes?.call_status
);
const callDirection = computed(
  () => props.chat.additional_attributes?.call_direction
);

const { labelKey: voiceLabelKey, listIconColor: voiceIconColor } =
  useVoiceCallStatus(callStatus, callDirection);

const inboxId = computed(() => props.chat.inbox_id);

const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

const isLiveChatWebWidget = computed(
  () => inbox.value?.channel_type === INBOX_TYPES.WEB
);

const typingUsers = computed(() => {
  try {
    return (
      store.getters['conversationTypingStatus/getUserList'](props.chat.id) || []
    );
  } catch (e) {
    return [];
  }
});

const contactTypingRecord = computed(() => {
  if (!isLiveChatWebWidget.value) return null;
  const sender = chatMetadata.value?.sender;
  const senderId = sender?.id;
  const bySender =
    senderId != null
      ? typingUsers.value.find(r => Number(r.id) === Number(senderId))
      : null;
  const fallback = typingUsers.value.find(
    r => String(r.type || '').toLowerCase() === 'contact'
  );
  return bySender || fallback || null;
});

const contactTypingPreview = computed(() => {
  const v = contactTypingRecord.value?.typingPreview;
  return String(v || '').trim();
});

const isContactTyping = computed(() => !!contactTypingRecord.value);

const showInboxName = computed(() => {
  return (
    !props.hideInboxName &&
    isInboxNameVisible.value &&
    inboxesList.value.length > 1
  );
});

const showMetaSection = computed(() => {
  return (
    showInboxName.value ||
    (props.showAssignee && assignee.value.name) ||
    props.chat.priority
  );
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const issueLabel = computed(() => {
  const issue = props.chat?.issue;
  if (!issue?.name) {
    return null;
  }

  return {
    title: issue.name,
    description: issue.description || '',
    color: issue.color || '#00c4ad',
  };
});

const showLabelsSection = computed(() => {
  return (
    props.chat.labels?.length > 0 || hasSlaPolicyId.value || issueLabel.value
  );
});

const messagePreviewClass = computed(() => {
  return [hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11'];
});

const isQaMonitorRoute = computed(() => {
  const routeName = String(route?.name || '');
  const routePath = String(route?.path || '');
  return (
    routeName.startsWith('qa_monitor') || routePath.includes('/qa-monitor')
  );
});

const isQaMonitorMode = computed(
  () => props.isQaMonitor || isQaMonitorRoute.value
);

const qaMonitorInboxId = computed(() => {
  const inboxIdFromRoute = route?.params?.inbox_id;
  return inboxIdFromRoute ? String(inboxIdFromRoute) : '';
});

const conversationPath = computed(() => {
  if (isAInboxViewRoute(String(route?.name || ''), true)) {
    return frontendURL(
      `accounts/${accountId.value}/inbox-view/conversation/${props.chat.id}`
    );
  }

  if (isQaMonitorMode.value) {
    if (qaMonitorInboxId.value) {
      return frontendURL(
        `accounts/${accountId.value}/qa-monitor/inbox/${qaMonitorInboxId.value}/conversations/${props.chat.id}`
      );
    }

    return frontendURL(
      `accounts/${accountId.value}/qa-monitor/conversations/${props.chat.id}`
    );
  }

  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      teamId: props.teamId,
      issueId: props.issueId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  // Handle Ctrl/Cmd + Click for new tab
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  if (isActiveChat.value) return;

  if (hasUnread.value && !isQaMonitorMode.value) {
    store.commit('UPDATE_MESSAGE_UNREAD_COUNT', {
      id: props.chat.id,
    });
  }

  const assignee = route?.query?.assignee;
  if (assignee) {
    router.push({ path, query: { assignee } });
  } else {
    router.push({ path });
  }
};

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, inbox.value.id);
  } else {
    emit('deSelectConversation', props.chat.id, inbox.value.id);
  }
};

const openContextMenu = e => {
  if (!props.enableContextMenu) return;
  e.preventDefault();
  emit('contextMenuToggle', true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [props.chat.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [props.chat.id]);
  closeContextMenu();
};

const onAssignTeam = team => {
  emit('assignTeam', team, props.chat.id);
  closeContextMenu();
};

const markAsUnread = () => {
  if (isQaMonitorMode.value) {
    closeContextMenu();
    return;
  }

  emit('markAsUnread', props.chat.id);
  closeContextMenu();
};

const markAsRead = () => {
  if (isQaMonitorMode.value) {
    closeContextMenu();
    return;
  }

  emit('markAsRead', props.chat.id);
  closeContextMenu();
};

const assignPriority = priority => {
  emit('assignPriority', priority, props.chat.id);
  closeContextMenu();
};

const deleteConversation = () => {
  emit('deleteConversation', props.chat.id);
  closeContextMenu();
};
</script>

<template>
  <!-- Collapsed View: Icon bar (avatar + unread badge only) -->
  <div
    v-if="isCollapsed"
    v-tooltip.right="currentContact.name"
    class="relative flex items-center justify-center w-16 py-3 border-b border-n-slate-3 cursor-pointer hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
    :class="{
      'bg-n-alpha-1 dark:bg-n-alpha-3 border-n-weak': isActiveChat,
    }"
    @click="onCardClick"
  >
    <div class="relative">
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      />
      <VipBadge
        :vip-level="currentContact.custom_attributes?.vip"
        size="sm"
        position="top-left"
        :offset-x="-8"
        :offset-y="-12"
      />
      <div
        v-if="hasUnread"
        class="wlab-unread-dot-wrap wlab-unread-dot--collapsed"
        aria-label="Unread"
      >
        <span
          class="css-1x6abro lc-Badge-module__badge___ZbEBU lc-Badge-module__badge--primary___wOrp- lc-Badge-module__badge--compact___AUaWg"
          aria-hidden="true"
        >
          <span
            data-testid="badge-dot"
            class="lc-Badge-module__badge__dot___MSVVf"
          />
        </span>
      </div>
    </div>
  </div>

  <!-- Expanded View: Full conversation card (LiveChat-inspired) -->
  <div
    v-else
    class="wlab-conv-card conversation group"
    :class="{
      'wlab-conv-card--active active': isActiveChat,
      'wlab-conv-card--selected': selected,
      'wlab-conv-card--has-unread': hasUnread,
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <!-- Avatar -->
    <div
      class="wlab-conv-avatar"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="36"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
        class="conv-avatar"
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <input
              :value="selected"
              :checked="selected"
              class="!m-0 cursor-pointer"
              type="checkbox"
              @change="onSelectConversation($event.target.checked)"
            />
          </label>
        </template>
      </Avatar>
      <VipBadge
        :vip-level="currentContact.custom_attributes?.vip"
        size="sm"
        position="top-left"
        :offset-x="-4"
        :offset-y="-8"
      />
    </div>

    <!-- Content -->
    <div class="wlab-conv-body">
      <!-- Row 1: Name (left) | Time (right) -->
      <div class="wlab-conv-row">
        <p
          class="wlab-conv-name"
          :class="{ 'wlab-conv-name--unread': hasUnread }"
        >
          {{ currentContact.name }}
        </p>
        <span class="wlab-conv-time">
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :show-date-when-not-today="isQaMonitorMode"
            :show-yesterday-when-not-today="isQaMonitorMode"
          />
        </span>
      </div>

      <!-- Row 2: Message Preview (left) | Unread Badge (right) -->
      <div class="wlab-conv-row">
        <template v-if="!isQaMonitorMode">
          <div v-if="callStatus" class="wlab-conv-msg">
            <span
              class="inline-block align-middle i-ph-phone-incoming"
              :class="[voiceIconColor]"
            />
            <span class="mx-1">{{ $t(voiceLabelKey) }}</span>
          </div>
          <div
            v-else-if="isContactTyping"
            class="wlab-conv-msg wlab-typing-sneakpeek"
          >
            <div class="wlab-typing-dots" data-test="typing-indicator">
              <span /><span /><span />
            </div>
            <p
              class="wlab-sneak-peek-text privacy-masker"
              data-testid="sneak-peek-text"
              dir="auto"
            >
              <span>{{ contactTypingPreview || '…' }}</span>
            </p>
          </div>
          <MessagePreview
            v-else-if="lastMessageInChat"
            :message="lastMessageInChat"
            class="wlab-conv-msg"
          />
          <p v-else class="wlab-conv-msg">
            <span>{{ $t('CHAT_LIST.NO_MESSAGES') }}</span>
          </p>
        </template>
        <div v-else class="wlab-conv-msg" />
        <div
          v-if="hasUnread"
          class="wlab-unread-dot-wrap wlab-conv-badge"
          aria-label="Unread"
        >
          <span
            class="css-1x6abro lc-Badge-module__badge___ZbEBU lc-Badge-module__badge--primary___wOrp- lc-Badge-module__badge--compact___AUaWg"
            aria-hidden="true"
          >
            <span
              data-testid="badge-dot"
              class="lc-Badge-module__badge__dot___MSVVf"
            />
          </span>
        </div>
      </div>

      <!-- Row 3: Tags (left, wrap) | Channel+Assignee (right) -->
      <div
        v-if="
          showInboxName || (showAssignee && assignee.name) || showLabelsSection
        "
        class="wlab-conv-meta"
      >
        <div v-if="showLabelsSection" class="wlab-conv-meta-left">
          <CardLabels
            :conversation-labels="chat.labels"
            :issue="chat.issue"
            :always-show-all="true"
            class="wlab-conv-tags"
          >
            <template #before>
              <SLACardLabel
                v-if="hasSlaPolicyId"
                :chat="chat"
                class="ltr:mr-1 rtl:ml-1"
              />
            </template>
          </CardLabels>
          <PriorityMark
            v-if="chat.priority"
            :priority="chat.priority"
            class="flex-shrink-0"
          />
        </div>

        <div class="wlab-conv-meta-right">
          <InboxName
            v-if="showInboxName"
            :inbox="inbox"
            class="wlab-conv-channel"
          />
          <span
            v-if="showInboxName && showAssignee && assignee.name"
            class="wlab-conv-dot"
            >·</span>
          <span v-if="showAssignee && assignee.name" class="wlab-conv-assignee">
            <fluent-icon icon="person" size="7" class="flex-shrink-0" />
            <span class="truncate">{{ assignee.name }}</span>
          </span>
        </div>
      </div>
    </div>

    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :conversation-url="conversationPath"
        :allowed-options="allowedContextMenuOptions"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @delete-conversation="deleteConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
</template>

<style>
/* ============================================
   LiveChat-inspired Conversation Card
   ============================================ */

.wlab-conv-card {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  cursor: pointer;
  width: 100%;
  padding: 12px 16px;
  min-height: 74px;
  opacity: 1;
  overflow: hidden;
  transition: box-shadow 0.2s cubic-bezier(0.64, 0, 0.35, 1);
}

/* ── Avatar ── */
.wlab-conv-avatar {
  align-self: flex-start;
  flex-shrink: 0;
  position: relative;
  margin: 3px 0 3px 3px;
}

.wlab-conv-avatar .conv-avatar {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  min-height: 36px !important;
}
.wlab-conv-avatar .conv-avatar img,
.wlab-conv-avatar .conv-avatar span {
  width: 36px !important;
  height: 36px !important;
  font-size: 15px !important;
  font-weight: 600 !important;
}

/* ── Body (content area) ── */
.wlab-conv-body {
  display: flex;
  flex-direction: column;
  padding: 0 4px 0 12px;
  overflow: hidden;
  flex: 1;
  min-width: 0;
  gap: 2px;
}

/* ── Shared row style ── */
.wlab-conv-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ── Name ── */
.wlab-conv-name {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow-wrap: normal;
  margin-bottom: 0;
  max-width: 100%;
  flex: 1;
  min-width: 0;
  color: var(--wlab-cl-text-primary, #bcbcc0);
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.1px;
}

.wlab-conv-name--unread {
  font-weight: 400;
}

/* ── Time ── */
.wlab-conv-time {
  flex-shrink: 0;
  font-size: 11px;
  line-height: 20px;
  color: var(--wlab-cl-text-muted, #767680);
  white-space: nowrap;
}
.wlab-conv-time * {
  font-size: 11px !important;
  color: inherit !important;
}

/* ── Message preview ── */
.wlab-conv-msg {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow-wrap: normal;
  /* Truncate earlier so ellipsis appears before reaching unread badge */
  max-width: calc(100% - 64px);
  flex: 1;
  min-width: 0;
  color: var(--wlab-cl-text-secondary, #767680);
  font-size: 11px;
  font-weight: 200;
  line-height: 20px;
  letter-spacing: -0.1px;
}
.wlab-conv-msg * {
  font-size: 11px !important;
  line-height: 20px !important;
  font-weight: 200 !important;
  color: inherit !important;
}

/* ── Unread badge ── */
.wlab-conv-badge {
  flex-shrink: 0;
  margin-left: auto;
}

/* Unread dot badge (no count) */
.wlab-unread-dot-wrap {
  display: flex;
  margin-left: auto;
}
.wlab-unread-dot--collapsed {
  position: absolute;
  right: -4px;
  bottom: -4px;
}

.wlab-unread-dot-wrap .css-1x6abro {
  margin: auto 0 auto auto;
  padding: 0;
}

.wlab-unread-dot-wrap .lc-Badge-module__badge___ZbEBU {
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 16px;
  font-weight: 700;
}

.wlab-unread-dot-wrap .lc-Badge-module__badge--primary___wOrp- {
  background-color: #ca0005;
  color: #ffffff;
}

.wlab-unread-dot-wrap .lc-Badge-module__badge--compact___AUaWg {
  border-radius: 16px;
  padding: 1px 5px;
  min-width: 16px;
  height: 16px;
}

.wlab-unread-dot-wrap
  .lc-Badge-module__badge--primary___wOrp-
  .lc-Badge-module__badge__dot___MSVVf {
  background-color: #ffffff;
}

.wlab-unread-dot-wrap .lc-Badge-module__badge__dot___MSVVf {
  border-radius: 6px;
  width: 6px;
  height: 6px;
  margin: 0 -2px;
}

/* ── Metadata row (tags left, channel/assignee right) ── */
.wlab-conv-meta {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
  width: 100%;
  min-width: 0;
}

.wlab-conv-meta-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.wlab-conv-meta-right {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  white-space: nowrap;
}
.wlab-conv-channel,
.wlab-conv-channel * {
  font-size: 8px !important;
  line-height: 16px !important;
  color: var(--wlab-cl-text-muted, #767680) !important;
  font-weight: 400 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.wlab-conv-channel svg,
.wlab-conv-channel .size-3 {
  width: 9px !important;
  height: 9px !important;
}

.wlab-conv-dot {
  color: var(--wlab-cl-text-muted, #767680);
  font-size: 8px;
  line-height: 16px;
  flex-shrink: 0;
}

.wlab-conv-assignee {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  max-width: 100px;
  flex-shrink: 0;
  font-size: 8px !important;
  line-height: 16px;
  color: var(--wlab-cl-text-muted, #767680);
  font-weight: 400 !important;
}
.wlab-conv-assignee svg {
  width: 7px !important;
  height: 7px !important;
}
.wlab-conv-assignee > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Inline labels (scrollable within meta row) ── */
.wlab-conv-tags {
  display: inline-flex;
  align-items: center;
  flex-shrink: 1;
  min-width: 0;
}
.wlab-conv-tags *,
.wlab-conv-tags .label {
  font-size: 9px !important;
  line-height: 1.2 !important;
  padding: 1px 4px !important;
  height: auto !important;
}
</style>
